import { v4 as uuidv4 } from 'uuid';

export const CONSENT_COOKIE_NAME = 'directordeck_consent_v2';
export const CONSENT_VERSION = 2;
export const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

export interface ConsentPreferences {
  analytics: boolean;
  diagnostics: boolean;
}

export interface ConsentChoice extends ConsentPreferences {
  version: typeof CONSENT_VERSION;
  savedAt: number;
  analyticsGrantId: string | null;
  diagnosticsGrantId: string | null;
}

function parsedGrantId(
  granted: boolean,
  value: unknown,
  savedAt: number,
  legacyPrefix: string,
): string | null | undefined {
  if (value === undefined) return granted ? `${legacyPrefix}:${savedAt}` : null;
  if (!granted) return value === null ? null : undefined;
  return typeof value === 'string' && value.length > 0 && value.length <= 128
    ? value
    : undefined;
}

export function parseConsentCookie(header: string): ConsentChoice | null {
  const pair = header
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE_NAME}=`));
  if (!pair) return null;

  try {
    const value: unknown = JSON.parse(
      decodeURIComponent(pair.slice(CONSENT_COOKIE_NAME.length + 1)),
    );
    if (!value || typeof value !== 'object') return null;
    const choice = value as Partial<ConsentChoice>;
    const age = Date.now() - (choice.savedAt ?? NaN);
    if (
      choice.version !== CONSENT_VERSION ||
      typeof choice.analytics !== 'boolean' ||
      typeof choice.diagnostics !== 'boolean' ||
      !Number.isFinite(age) ||
      age < 0 ||
      age >= CONSENT_MAX_AGE_MS
    ) {
      return null;
    }
    const analyticsGrantId = parsedGrantId(
      choice.analytics,
      choice.analyticsGrantId,
      choice.savedAt as number,
      'legacy',
    );
    const diagnosticsGrantId = parsedGrantId(
      choice.diagnostics,
      choice.diagnosticsGrantId,
      choice.savedAt as number,
      'legacy-diagnostics',
    );
    if (analyticsGrantId === undefined || diagnosticsGrantId === undefined)
      return null;
    return {
      version: CONSENT_VERSION,
      analytics: choice.analytics,
      diagnostics: choice.diagnostics,
      savedAt: choice.savedAt as number,
      // Existing v2 cookies predate grant IDs. Keep each grant valid until
      // that category is withdrawn, when the next grant gets a fresh ID.
      analyticsGrantId,
      diagnosticsGrantId,
    };
  } catch {
    return null;
  }
}

export function readConsentChoice(): ConsentChoice | null {
  if (typeof document === 'undefined') return null;
  return parseConsentCookie(document.cookie);
}

export function saveConsentChoice(
  preferences: ConsentPreferences,
): ConsentChoice {
  const previous = readConsentChoice();
  const choice: ConsentChoice = {
    version: CONSENT_VERSION,
    ...preferences,
    savedAt: Date.now(),
    analyticsGrantId: preferences.analytics
      ? previous?.analytics
        ? previous.analyticsGrantId
        : uuidv4()
      : null,
    diagnosticsGrantId: preferences.diagnostics
      ? previous?.diagnostics
        ? previous.diagnosticsGrantId
        : uuidv4()
      : null,
  };
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = 'directordeck_consent_v1=; Max-Age=0; Path=/; SameSite=Lax';
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(choice))}; Max-Age=${Math.floor(CONSENT_MAX_AGE_MS / 1000)}; Path=/; SameSite=Lax${secure}`;
  return choice;
}

export function mayLoadAnalytics(): boolean {
  return readConsentChoice()?.analytics === true;
}

export function mayLoadDiagnostics(): boolean {
  return readConsentChoice()?.diagnostics === true;
}
